/**
 * Browser MarketDataService.
 *
 * Fetches token availability + contract info through our own API (served by
 * `packages/api`), never directly from exchange REST, so we avoid CORS issues
 * and have a single place to cache/shape the data.
 *
 * Caching policy:
 *  token-available  → 30 min  (localStorage key `avail-all:{symbol}`)
 *  contract-info    → 24 h    (localStorage key `contract:{exchange}:{symbol}`)
 *
 * Exported:
 *  getClientMarketDataService — lazy singleton used by hooks
 */

import { ResultCache } from "./instrument-cache";
import { LocalStorageCacheStorage } from "./cache-storage";
import type {
  ContractInfo,
  ExchangeAvailabilityMap,
  ExchangeCode,
  TokenAvailability,
} from "./market-data.types";
import { SUPPORTED_EXCHANGES } from "./market-data.types";

const TOKEN_AVAILABLE_TTL = 30 * 60 * 1000;
const CONTRACT_INFO_TTL = 24 * 60 * 60 * 1000;

/**
 * Base URL for the API that serves /market-data/* endpoints. The miniapp's
 * `VITE_API_URL` already includes the `/api` prefix (e.g.
 * `http://localhost:3001/api`), so we just append the route.
 */
const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  "http://localhost:3001/api";

interface ApiSuccess<T> {
  success: true;
  data: T;
}
interface ApiError {
  success: false;
  error: string;
}
type ApiResponse<T> = ApiSuccess<T> | ApiError;

export interface IMarketDataService {
  checkTokenAvailable(
    exchange: ExchangeCode,
    symbol: string
  ): Promise<TokenAvailability>;
  /** One round-trip that checks every supported exchange for the symbol. */
  checkTokenAvailableAll(symbol: string): Promise<ExchangeAvailabilityMap>;
  getContractInfo(
    exchange: ExchangeCode,
    symbol: string
  ): Promise<ContractInfo | null>;
}

export class BrowserMarketDataService implements IMarketDataService {
  private readonly tokenAvailBatchCache: ResultCache<ExchangeAvailabilityMap>;
  private readonly contractInfoCache: ResultCache<ContractInfo>;

  constructor() {
    const storage = new LocalStorageCacheStorage();
    storage.sweepExpiredTokenAvailability(TOKEN_AVAILABLE_TTL);
    this.tokenAvailBatchCache = new ResultCache<ExchangeAvailabilityMap>(
      TOKEN_AVAILABLE_TTL,
      storage
    );
    this.contractInfoCache = new ResultCache<ContractInfo>(
      CONTRACT_INFO_TTL,
      storage
    );
  }

  async checkTokenAvailable(
    exchange: ExchangeCode,
    symbol: string
  ): Promise<TokenAvailability> {
    // One `avail-all:{symbol}` row in localStorage — no per-exchange keys.
    const map = await this.checkTokenAvailableAll(symbol);
    return map[exchange];
  }

  async checkTokenAvailableAll(
    symbol: string
  ): Promise<ExchangeAvailabilityMap> {
    const batchKey = `avail-all:${symbol.toUpperCase()}`;
    const batchCached = this.tokenAvailBatchCache.get(batchKey);
    if (batchCached) return batchCached;

    const res = await fetch(
      `${API_BASE_URL}/market-data/token-available-all/${encodeURIComponent(symbol)}`
    );
    const json = (await res.json()) as ApiResponse<ExchangeAvailabilityMap>;
    if (!json.success) throw new Error(json.error);

    // Ensure every supported exchange has an entry so callers can safely index.
    const filled = fillMissingExchanges(symbol, json.data);
    this.tokenAvailBatchCache.set(batchKey, filled);
    return filled;
  }

  async getContractInfo(
    exchange: ExchangeCode,
    symbol: string
  ): Promise<ContractInfo | null> {
    const key = `contract:${exchange}:${symbol.toUpperCase()}`;
    const cached = this.contractInfoCache.get(key);
    if (cached) return cached;

    const res = await fetch(
      `${API_BASE_URL}/market-data/contract-info/${exchange}/${encodeURIComponent(symbol)}`
    );

    if (res.status === 404) return null;

    const json = (await res.json()) as ApiResponse<ContractInfo>;
    if (!json.success) throw new Error(json.error);

    this.contractInfoCache.set(key, json.data);
    return json.data;
  }
}

function fillMissingExchanges(
  symbol: string,
  partial: Partial<ExchangeAvailabilityMap>
): ExchangeAvailabilityMap {
  const normalized = symbol.toUpperCase();
  const out = { ...partial } as ExchangeAvailabilityMap;
  for (const ex of SUPPORTED_EXCHANGES) {
    if (!out[ex]) {
      out[ex] = {
        symbol: normalized,
        available: false,
        status: "NOT_FOUND",
      };
    }
  }
  return out;
}

let _clientInstance: BrowserMarketDataService | null = null;
export function getClientMarketDataService(): BrowserMarketDataService {
  if (!_clientInstance) {
    _clientInstance = new BrowserMarketDataService();
  }
  return _clientInstance;
}

export function isValidExchange(value: string): value is ExchangeCode {
  return SUPPORTED_EXCHANGES.includes(value as ExchangeCode);
}
